import { useCallback } from 'react';
import { useGate } from 'statsig-react';
import { gql } from '@soundxyz/gql-string';
import type { Maybe } from '@soundxyz/utils';
import { useAuth } from '@context/auth/base';
import {
  type ExecutionResultWithData,
  ReleasesArePresavedDocument,
  type ReleasesArePresavedQuery,
  useQuery,
} from '@graphql/reactQuery';
import { createBatchingInputStore } from '@store/batchInput';
import { FEATURE_GATES } from '@utils/constants';

const { useBatchedKey, batchedKeys } = createBatchingInputStore({
  chunkLimit: 50,
});

export function getReleasePresavedBatchedKeys({ releaseId }: { releaseId: string }) {
  return batchedKeys.keys.get(releaseId);
}

/*#__PURE__*/ gql(/* GraphQL */ `
  query ReleasesArePresaved($releaseIds: [UUID!]!) {
    userReleaseStatus(input: { releaseIds: $releaseIds }) {
      isPreSaved
      releaseId
    }
  }
`);

export function useIsReleasePresaved({
  releaseId,
  enabled = true,
}: {
  releaseId: Maybe<string>;
  enabled?: boolean;
}) {
  const ids = useBatchedKey({
    key: releaseId,
  });

  const { value: isSpotifyPresaveEnabled } = useGate(FEATURE_GATES.USE_SPOTIFY_PRESAVE);

  const { authUserAddress, isAuthenticatedWithAPI } = useAuth();

  const selectUserReleaseStatusPresaved = useCallback(
    (data: ExecutionResultWithData<ReleasesArePresavedQuery>) => {
      return data.data.userReleaseStatus.find(v => v.releaseId === releaseId)?.isPreSaved ?? false;
    },
    [releaseId],
  );

  const { data: isReleasePresaved, setQueryData } = useQuery(ReleasesArePresavedDocument, {
    variables: !!ids && {
      releaseIds: ids,
    },
    enabled: enabled && isSpotifyPresaveEnabled && isAuthenticatedWithAPI,
    staleTime: 0,
    filterQueryKey: {
      authUserAddress,
    },
    select: selectUserReleaseStatusPresaved,
    keepPreviousData: true,
  });

  return { isReleasePresaved, setQueryData };
}
